import {
  queryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { asvParams } from '@components/features/AsvScans/params';
import {
  AsvConfig,
  AsvConfigResponse,
  AsvException,
  AsvReport,
  AsvScan,
  AsvScanRun,
  AsvSchedule,
  AsvTarget,
  AsvVulnerability,
  CreateAsvConfigRequest,
  CreateAsvExceptionRequest,
  CreateAsvTargetRequest,
  DownloadAsvReportRequest,
  GenerateAsvReportRequest,
  GetAsvVulnerabilitiesParams,
  PaginatedParams,
  PaginatedResponse,
  RegisterAsvInterestRequest,
  StartAsvScanRequest,
  UpdateAsvCompanyRequest,
  UpdateAsvConfigRequest,
  UpdateAsvTargetRequest,
} from '@components/features/AsvScans/types';
import {
  isReportProcessing,
  isScanInProgress,
} from '@components/features/AsvScans/utils';
import { api } from '@services/apiService';
import { isHttpNotFoundError } from '@utils/error';
import { useStrongParams } from '@utils/params';
import { uniqBy } from '@utils/uniq';

export const asvKeys = {
  allConfigs: (teamUuid: string) => ['team', teamUuid, 'asv'],
  config: (teamUuid: string, appUuid: string) => [
    ...asvKeys.allConfigs(teamUuid),
    'app',
    appUuid,
  ],

  allTargets: (teamUuid: string, appUuid: string) => [
    ...asvKeys.config(teamUuid, appUuid),
    'targets',
  ],

  allScans: (teamUuid: string, appUuid: string) => [
    ...asvKeys.config(teamUuid, appUuid),
    'scans',
  ],
  paginatedScans: (
    teamUuid: string,
    appUuid: string,
    params: PaginatedParams
  ) => [...asvKeys.allScans(teamUuid, appUuid), 'page', params],
  scanRun: (teamUuid: string, appUuid: string, scanRunUuid: string) => [
    ...asvKeys.allScans(teamUuid, appUuid),
    'runs',
    scanRunUuid,
  ],

  allSchedules: (teamUuid: string, appUuid: string) => [
    ...asvKeys.config(teamUuid, appUuid),
    'schedules',
  ],

  report: (teamUuid: string, appUuid: string, reportUuid: string) => [
    ...asvKeys.config(teamUuid, appUuid),
    'reports',
    reportUuid,
  ],
  reportVulnerabilities: (
    teamUuid: string,
    appUuid: string,
    reportUuid: string
  ) => [...asvKeys.report(teamUuid, appUuid, reportUuid), 'vulnerabilities'],

  allVulnerabilities: (
    teamUuid: string,
    appUuid: string,
    params?: GetAsvVulnerabilitiesParams
  ) => [
    ...asvKeys.config(teamUuid, appUuid),
    'vulnerabilities',
    `params=${JSON.stringify(params)}`,
  ],
  vulnerability: (
    teamUuid: string,
    appUuid: string,
    vulnerabilityUuid: string
  ) => [
    ...asvKeys.config(teamUuid, appUuid),
    'vulnerabilities',
    vulnerabilityUuid,
  ],

  exception: (teamUuid: string, appUuid: string, exceptionUuid: string) => [
    ...asvKeys.config(teamUuid, appUuid),
    'exceptions',
    exceptionUuid,
  ],
};

export const asvFilters = {
  allTargets: (teamUuid: string, appUuid: string) => ({
    queryKey: asvKeys.allTargets(teamUuid, appUuid),
    exact: true,
  }),
  allScans: (teamUuid: string, appUuid: string) => ({
    queryKey: [...asvKeys.allScans(teamUuid, appUuid), 'page'],
    exact: false,
  }),
  allSchedules: (teamUuid: string, appUuid: string) => ({
    queryKey: asvKeys.allSchedules(teamUuid, appUuid),
    exact: false,
  }),
};

////////////////////////////
// QUERIES
////////////////////////////

export function teamAsvConfigsQueryOptions(teamUuid: string) {
  return queryOptions({
    queryKey: asvKeys.allConfigs(teamUuid),
    queryFn: async () => {
      try {
        const response = await api.get<AsvConfig[]>(`teams/${teamUuid}/asv`);
        return await response.json();
      } catch (error) {
        if (isHttpNotFoundError(error)) {
          return [];
        }
        throw error;
      }
    },
  });
}

export function useTeamAsvConfigsQuery() {
  const { teamUuid } = useStrongParams(asvParams);

  return useQuery(teamAsvConfigsQueryOptions(teamUuid));
}

export function asvConfigQueryOptions(teamUuid: string, appUuid: string) {
  return queryOptions({
    queryKey: asvKeys.config(teamUuid, appUuid),
    queryFn: async () => {
      try {
        const response = await api.get<AsvConfigResponse>(
          `apps/${appUuid}/asv`
        );
        return await response.json();
      } catch (error) {
        if (isHttpNotFoundError(error)) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useAsvConfigQuery(teamUuid: string, appUuid: string) {
  return useQuery({
    ...asvConfigQueryOptions(teamUuid, appUuid),
    refetchOnWindowFocus: false,
  });
}

export function useAsvTargetsInfiniteQuery(
  options?: UseInfiniteQueryOptions<PaginatedResponse<AsvTarget>>
) {
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useInfiniteQuery({
    queryKey: asvKeys.allTargets(teamUuid, appUuid),
    queryFn: async ({ pageParam = 0 }) => {
      try {
        const response = await api.get<PaginatedResponse<AsvTarget>>(
          `apps/${appUuid}/asv/scan-configs`,
          {
            searchParams: {
              page: pageParam,
              pageSize: 25,
            },
          }
        );
        return await response.json();
      } catch (error) {
        if (isHttpNotFoundError(error)) {
          return { data: [], nextPage: null, previousPage: null, total: 0 };
        }
        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    ...options,
  });
}

export function useAsvScanRunsPaginatedQuery(
  params: PaginatedParams,
  options?: UseQueryOptions<PaginatedResponse<AsvScanRun>>
) {
  const { teamUuid, appUuid } = useStrongParams(asvParams);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: asvKeys.paginatedScans(teamUuid, appUuid, params),
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AsvScanRun>>(
        `apps/${appUuid}/asv/scan-runs`,
        {
          searchParams: {
            name: params.targetName,
            page: params.page,
            pageSize: 25,
          },
        }
      );
      const page = await response.json();
      page.data.forEach((scanRun) => {
        queryClient.setQueryData(
          asvKeys.scanRun(teamUuid, appUuid, scanRun.id),
          scanRun
        );
      });
      return page;
    },
    refetchInterval(data) {
      if (!data) return false;
      const hasRunningScans = data.data.some(isScanInProgress);
      if (hasRunningScans) {
        // Poll every 10s while scans are running
        return 10_000;
      } else {
        return false;
      }
    },
    ...options,
  });
}

export function asvScanRunQueryOptions(
  teamUuid: string,
  appUuid: string,
  scanRunUuid: string
) {
  return queryOptions({
    queryKey: asvKeys.scanRun(teamUuid, appUuid, scanRunUuid),
    queryFn: async () => {
      const response = await api.get<AsvScanRun>(
        `apps/${appUuid}/asv/scan-runs/${scanRunUuid}`
      );
      return await response.json();
    },
  });
}

export function useAsvScanRunQuery(scanRunUuid: string) {
  const { teamUuid, appUuid } = useStrongParams(asvParams);
  return useQuery({
    ...asvScanRunQueryOptions(teamUuid, appUuid, scanRunUuid),
    refetchInterval(data) {
      if (!data) return false;
      if (isScanInProgress(data)) {
        // Poll every 10s while scan is running
        return 10_000;
      } else {
        return false;
      }
    },
  });
}

export function useAsvReportQuery(
  reportUuid: string,
  options?: UseQueryOptions<AsvReport>
) {
  const { teamUuid, appUuid } = useStrongParams(asvParams);
  return useQuery({
    queryKey: asvKeys.report(teamUuid, appUuid, reportUuid),
    queryFn: async () => {
      const response = await api.get<{ data: AsvReport }>(
        `apps/${appUuid}/asv/reports/${reportUuid}`
      );
      const json = await response.json();
      return json.data;
    },
    refetchInterval(data) {
      if (data && isReportProcessing(data)) {
        // Poll every 1s while report is processing
        return 1_000;
      } else {
        return false;
      }
    },
    enabled: !!reportUuid,
    ...options,
  });
}

export function useAsvReportVulnerabilitiesInfiniteQuery(
  reportUuid: string,
  options?: UseInfiniteQueryOptions<PaginatedResponse<AsvVulnerability>>
) {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useInfiniteQuery({
    queryKey: asvKeys.reportVulnerabilities(teamUuid, appUuid, reportUuid),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get<PaginatedResponse<AsvVulnerability>>(
        `apps/${appUuid}/asv/reports/${reportUuid}/vulnerabilities`,
        {
          searchParams: {
            page: pageParam,
            pageSize: 100,
          },
        }
      );
      const page = await response.json();
      const data = uniqBy(page.data, 'id');
      data.forEach((vulnerability) => {
        queryClient.setQueryData(
          asvKeys.vulnerability(teamUuid, appUuid, vulnerability.id),
          vulnerability
        );
      });
      return { ...page, data };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    ...options,
  });
}

export function useAsvVulnerabilitiesPaginatedQuery<
  T = PaginatedResponse<AsvVulnerability>,
>(
  params: GetAsvVulnerabilitiesParams,
  options?: UseQueryOptions<PaginatedResponse<AsvVulnerability>, unknown, T>
) {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useQuery({
    queryKey: asvKeys.allVulnerabilities(teamUuid, appUuid, params),
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        pageSize: '25',
      });
      if (params?.severity?.length) {
        searchParams.append('severity', params.severity.join(','));
      } else {
        searchParams.append('severity', 'low,medium,high');
      }
      if (params?.sortBy) {
        searchParams.append('sortBy', params.sortBy);
      }
      if (params?.order) {
        searchParams.append('order', params.order);
      }
      const response = await api.get<PaginatedResponse<AsvVulnerability>>(
        `apps/${appUuid}/asv/vulnerabilities`,
        { searchParams }
      );
      const page = await response.json();
      const data = uniqBy(page.data, 'id');
      data.forEach((vulnerability) => {
        queryClient.setQueryData(
          asvKeys.vulnerability(teamUuid, appUuid, vulnerability.id),
          vulnerability
        );
      });
      return { ...page, data };
    },
    ...options,
  });
}

export function asvVulnerabilityQueryOptions(
  teamUuid: string,
  appUuid: string,
  vulnerabilityUuid: string
) {
  return queryOptions({
    queryKey: asvKeys.vulnerability(teamUuid, appUuid, vulnerabilityUuid),
    queryFn: async () => {
      const response = await api.get<AsvVulnerability>(
        `apps/${appUuid}/asv/vulnerabilities/${vulnerabilityUuid}`
      );
      return await response.json();
    },
  });
}

export function asvExceptionQueryOptions(
  teamUuid: string,
  appUuid: string,
  exceptionUuid: string
) {
  return queryOptions({
    queryKey: asvKeys.exception(teamUuid, appUuid, exceptionUuid),
    queryFn: async () => {
      const response = await api.get<{ data: AsvException }>(
        `apps/${appUuid}/asv/exceptions/${exceptionUuid}`
      );
      const json = await response.json();
      return json.data;
    },
  });
}

export function useAsvAllSchedulesQuery() {
  const { teamUuid, appUuid } = useStrongParams(asvParams);
  return useQuery({
    queryKey: asvKeys.allSchedules(teamUuid, appUuid),
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AsvSchedule>>(
        `apps/${appUuid}/asv/schedules`
      );
      return await response.json();
    },
  });
}

////////////////////////////
// MUTATIONS
////////////////////////////

export function useRegisterAsvInterestMutation(
  teamUuid: string,
  appUuid: string
) {
  return useMutation({
    mutationFn: async (request: RegisterAsvInterestRequest) => {
      await api.post(`apps/${appUuid}/asv/interest`, {
        json: request,
      });
    },
  });
}

export function useCreateAsvConfigMutation(teamUuid: string, appUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateAsvConfigRequest) => {
      const response = await api.post(`apps/${appUuid}/asv`, {
        json: request,
      });
      return await response.json();
    },
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: asvKeys.config(teamUuid, appUuid),
      });
    },
  });
}

export function useUpdateAsvConfigMutation(teamUuid: string, appUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateAsvConfigRequest) => {
      const response = await api.put(
        `admin/teams/${teamUuid}/apps/${appUuid}/asv`,
        {
          json: request,
        }
      );
      return await response.json();
    },
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: asvKeys.config(teamUuid, appUuid),
      });
    },
  });
}

export function useUpdateAsvCompanyMutation(teamUuid: string, appUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateAsvCompanyRequest) => {
      await api.put(`apps/${appUuid}/asv/organization`, {
        json: request,
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: asvKeys.config(teamUuid, appUuid),
      });
    },
  });
}

export function useCreateAsvTargetMutation() {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useMutation({
    mutationFn: async (request: CreateAsvTargetRequest) => {
      const response = await api.post<AsvScan>(
        `apps/${appUuid}/asv/scan-configs`,
        {
          json: request,
        }
      );
      return await response.json();
    },
    onSuccess() {
      void queryClient.invalidateQueries(
        asvFilters.allTargets(teamUuid, appUuid)
      );
      void queryClient.invalidateQueries(
        asvFilters.allScans(teamUuid, appUuid)
      );
      void queryClient.invalidateQueries(
        asvFilters.allSchedules(teamUuid, appUuid)
      );
    },
  });
}

export function useUpdateAsvTargetMutation() {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useMutation({
    mutationFn: async ({ id, ...request }: UpdateAsvTargetRequest) => {
      const response = await api.put<AsvTarget>(
        `apps/${appUuid}/asv/targets/${id}`,
        {
          json: request,
        }
      );
      return await response.json();
    },
    onSuccess() {
      void queryClient.invalidateQueries(
        asvFilters.allTargets(teamUuid, appUuid)
      );
    },
  });
}

export function useStartAsvScanMutation() {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useMutation({
    mutationFn: async (request: StartAsvScanRequest) => {
      const response = await api.post<AsvScanRun>(
        `apps/${appUuid}/asv/scan-runs/${request.targetName}/start`
      );
      const scanRun = await response.json();
      return scanRun;
    },
    onSuccess(scanRun) {
      queryClient.setQueryData(
        asvKeys.scanRun(teamUuid, appUuid, scanRun.id),
        scanRun
      );
      void queryClient.invalidateQueries(
        asvFilters.allScans(teamUuid, appUuid)
      );
    },
  });
}

export function useStopAsvScanMutation() {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);
  return useMutation({
    mutationFn: async (scanRunUuid: string) => {
      await api.post(`apps/${appUuid}/asv/scan-runs/${scanRunUuid}/stop`);
    },
    onSuccess(_, scanRunUuid) {
      void queryClient.invalidateQueries({
        queryKey: asvKeys.scanRun(teamUuid, appUuid, scanRunUuid),
      });
      void queryClient.invalidateQueries(
        asvFilters.allScans(teamUuid, appUuid)
      );
    },
  });
}

export function useGenerateAsvReportMutation() {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);

  return useMutation({
    mutationFn: async ({ reportUuid, type }: GenerateAsvReportRequest) => {
      const response = await api.post(
        `apps/${appUuid}/asv/reports/${reportUuid}/generate`,
        {
          json: { type },
        }
      );
      return await response.json();
    },
    async onSuccess(_, request) {
      await queryClient.invalidateQueries({
        queryKey: asvKeys.report(teamUuid, appUuid, request.reportUuid),
      });
    },
  });
}

export function useDownloadAsvReportMutation() {
  const { appUuid } = useStrongParams(asvParams);
  return useMutation({
    mutationFn: async ({ reportUuid, type }: DownloadAsvReportRequest) => {
      const response = await api.get(
        `apps/${appUuid}/asv/reports/${reportUuid}/download`,
        {
          searchParams: { type },
        }
      );
      return await response.blob();
    },
  });
}

export function useCreateAsvExceptionMutation() {
  const queryClient = useQueryClient();
  const { teamUuid, appUuid } = useStrongParams(asvParams);
  return useMutation({
    mutationFn: async ({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      vulnerabilityUuid,
      ...request
    }: CreateAsvExceptionRequest) => {
      const response = await api.post<{ data: AsvException }>(
        `apps/${appUuid}/asv/exceptions`,
        {
          json: request,
        }
      );
      return await response.json();
    },
    onSuccess({ data }, request) {
      // Optimistically update the exception
      queryClient.setQueryData(
        asvKeys.exception(teamUuid, appUuid, data.id),
        data
      );
      // Invalidate the vulnerability to update the exception status
      void queryClient.invalidateQueries({
        queryKey: asvKeys.vulnerability(
          teamUuid,
          appUuid,
          request.vulnerabilityUuid
        ),
      });
    },
  });
}
